<template>
	<div v-if="selectedProject" class="project-top-bar display-flex flex-row width-100 flex-justify-space-between flex-align-center">
		<div class="display-flex flex-row height-100">
			<router-link :to="plyButtonTo">
				<AnemicButton class="ply-logo-container flex-centerize">
					<SIcon name="ply" :width="19" />
				</AnemicButton>
			</router-link>
			<div class="display-flex flex-column ml-6" style="min-width: 300px">
				<div class="text-field-description display-flex flex-row flex-align-center mt-2 mb-n2">
					<SpaceIconCard>
						<SIcon :name="projectIconIndicatorName" width="10" />
					</SpaceIconCard>
					<div class="item-name ml-1">
						{{ projectTitle }}
					</div>
				</div>
				<ProjectNameEditableTextField
					isEditOnClick
					@onSave="onSave"
					:text="flowTitle"
					placeholder="Enter Name"
					:hasEditPermissions="hasEditPermissions"
				/>
			</div>
		</div>
		<div v-if="isInterfaceFocusedLayoutModeEnabled"><LayoutModeSwitch v-model="isNormalLayout" /></div>
		<Row :class="{ 'faded pointer-events-none': isLeftSideInDrawerSpecialEditMode }" class="pr-5 flex-align-center py-4 height-100 py-4">
			<AppLoader class="mr-2 flex-centerize" title="Saving..." v-if="moveNodeLoading" />
			<WorkflowVersionHistoryMenu :workflow="editWorkflow" :hasEditPermissions="hasEditPermissions" />
			<Col class="divider mx-3" />
			<AppTooltip position="bottom" :text="taskLogButtonTooltip">
				<AnemicButton
					tile
					@click="openTaskLog"
					v-if="canViewTaskLog"
					class="builder-task-log-button pa-0"
					:disabled="!hasWorkflowEverBeenPublished"
				>
					<SIcon name="builder-task-log" :height="18" :width="18" />
				</AnemicButton>
			</AppTooltip>
			<div v-if="canViewTaskLog" class="ml-3" />
			<AppTooltip position="bottom" text="Feature settings">
				<AnemicButton tile :disabled="!hasEditPermissions" @click="onSettingsClick" class="setting-button">
					<SIcon :width="18" name="top-bar-settings" />
				</AnemicButton>
			</AppTooltip>
			<Col class="divider mx-3" />
			<ShareButton :workflowId="editWorkflow?.id" class="share-button" :flowType="editWorkflow?.flow_type"> Share </ShareButton>
			<TestButton
				class="ml-3"
				v-if="showTestButton"
				:canPublish="canPublish"
				:workflowId="editWorkflow.id"
				:needPublish="isPublishNeeded"
				:showWarningIcon="!canPublish"
				:problematicNodes="problematicNodes"
				:allowedToPublish="allowedToPublish"
				@test-flow="testFlow"
			/>
			<PublishButton
				class="ml-3"
				:canPublish="canPublish"
				:needPublish="isPublishNeeded"
				:showWarningIcon="!canPublish"
				:problematicNodes="problematicNodes"
				:allowedToPublish="allowedToPublish"
				:showCloseBuilderButton="isLoadedFromExtension || isLoadedFromEmbedded"
				@close-builder="closeBuilder"
			/>
		</Row>
		<Row v-if="isLeftSideInDrawerSpecialEditMode" class="flex-align-center left-side-special-editor-indication flex-centerize">
			<Row class="indication-container px-5 flex-align-center">
				<h3 class="reg mr-2">{{ isVersionHistoryOpen ? 'Version history view' : 'Debug mode' }}</h3>
				<h3 @click="closeLeftSideDrawerSpecialEditor" class="reg link">Back to editing</h3>
			</Row>
		</Row>
	</div>
</template>

<script>
/* eslint-disable */
import SpaceIconCard from '@/views/AppLayout/ManageAndCreate/SpaceIconCard.vue';
import ShareButton from '../share/ShareButton';
import PublishButton from '../PublishButton';
import { mapGetters, mapActions } from 'vuex';
import AppTooltip from '@/components/common/AppTooltip';
import { getService } from '@/store/modules/apps/getters';
import WorkflowVersionHistoryMenu from '@/components/WorkflowVersionHistory/WorkflowVersionHistoryMenu';
import { isInterfaceFocusedLayoutModeEnabled, isTestFeatureAllowed, showRevertDraftButton } from '@/store/modules/featureFlags/getters';
import { editWorkflow, nodesData, nodesDataArray, rootNode, isOfTypeFeature, isOfTypeTool } from '@/store/modules/selectedWorkflow/getters';
import { updateWorkflow } from '@/store/modules/selectedWorkflow/actions';
import ProjectNameEditableTextField from '@/components/Project/ProjectNameEditableTextField';
import AppDialogManagerService, { openTestURLToolDialog } from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import ProjectService from '@/utils/projects-service';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { selectedProject } from '@/store/modules/selectedProject/getters';
import TestButton from '../TestButton.vue';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { isURLTool } from '@/utils/workflow-tool-service';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import { getAction } from '@/store/modules/actions/getters';
import { isStatusPublishValid } from '../../BlockEditor/block-editor-service';
import { selectProjectId } from '@/store/modules/selectedProject/actions';
import { moveNodeLoading } from '@/store/modules/treeBlocks/getters';
import { setLeftSideDrawerState } from '@/store/modules/leftSideDrawer/actions';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { BUILDER_DEBUG_MODE_ROUTE_NAME, IS_LOADED_FROM_EXTENSION_PARAM, IS_EMBEDDED_PARAM } from '@/router/router.const.js';
import AppService from '@/utils/AppService';
import WorkflowService from '@/utils/WorkflowService';
import WorkflowVersionService from '@/components/WorkflowVersionHistory/workflow-version-service';
import { useSelectedWorkflowVersionId } from '@/store/modules/workflows/modules/versions/getters.js';
import { useIsTaskLogOpen } from '@/store/modules/analytics/getters.js';
import IframeService from '@/utils/iframe-service';
import { EmbeddedAppActionTypes, useEmbeddedApp } from '@/views/SideBar/embedded-app-entry-service';
import { useFlowLayoutMode } from '@/components/FlowTree/composables';
import LayoutModeSwitch from './LayoutModeSwitch.vue';
import { GETTING_STARTED_ROUTE_NAME } from '@/router/router.const';
const TEST_FEATURE_EXTENSION_MINIMAL_VERSION = '0.1.26';
const canShowTestFeatureButton = () => ExtensionCommunicationService.isExtensionVersionGTEToVersion(TEST_FEATURE_EXTENSION_MINIMAL_VERSION);
export default {
	name: 'TopBar',
	props: {
		projectIdToReturn: { type: [Number, String], default: null },
		projectId: {type: Number}
	},
	components: {
		AppTooltip,
		WorkflowVersionHistoryMenu,
		ShareButton,
		PublishButton,
		ProjectNameEditableTextField,
		SpaceIconCard,
		TestButton,
		LayoutModeSwitch,
	},
	computed: {
		...mapGettersShorthand({
			selectedProject,
			nodesDataArray,
			projectHasPermission,
			getAction,
			isTestFeatureAllowed,
			isOfTypeFeature,
			isOfTypeTool,
			moveNodeLoading,
			useSelectedWorkflowVersionId,
			useIsTaskLogOpen,
			isInterfaceFocusedLayoutModeEnabled,
		}),
		...mapGetters([
			showRevertDraftButton.fullPath,
			nodesData.fullPath,
			getService.fullPath,
			editWorkflow.fullPath,
			rootNode.fullPath,
			canEditWorkflow.fullPath,
		]),
		plyButtonTo() {
			return { name: GETTING_STARTED_ROUTE_NAME, params: { spaceId: this.projectIdToReturn || this.selectedProject.id } };
		},
		showTestButton() {
			return (
				((((this.isLoadedFromExtension && this.extSupportTest) || this.isLoadedFromEmbedded) && this.isOfTypeFeature) || 
				(this.isOfTypeTool && isURLTool(this.editWorkflow))) &&
				this.isTestFeatureAllowed
			);
		},
		isLeftSideInDrawerSpecialEditMode() {
			return this.isTaskLogOpen || this.isVersionHistoryOpen;
		},
		projectTitle() {
			return this.selectedProject?.title;
		},
		showRevertDraftButton() {
			return this[showRevertDraftButton.fullPath];
		},
		projectIconIndicatorName() {
			return `${this.isPersonalProject ? 'personal' : 'collaborative'}-space`;
		},
		isPersonalProject() {
			return ProjectService.isPersonalProject(this.selectedProject);
		},
		asyncEnabled: {
			get() {
				return this.editWorkflow?.async_enabled;
			},
			set(value) {
				this[updateWorkflow.fullPath]({ data: { async_enabled: value } });
			},
		},
		editWorkflow() {
			return this[editWorkflow.fullPath];
		},
		hasEditPermissions() {
			if (!this.editWorkflow?.id) {
				return false;
			}
			return this[canEditWorkflow.fullPath](this.editWorkflow.id);
		},
		isPublishNeeded() {
			return this.editWorkflow && (this.editWorkflow.draft_pending_changes_flag || !this.hasWorkflowEverBeenPublished);
		},
		showWarningIcon() {
			return !this.canPublish;
		},
		canPublish() {
			return Object.keys(this.problematicNodes).length === 0;
		},
		problematicNodes() {
			return this.relevantNodes.filter((workflowNode) => !this.isNodeStatusPublishValid(workflowNode));
		},
		relevantNodes() {
			return this.nodesDataArray.filter((node) => !WorkflowNodeService.isNodeOrphan(node));
		},
		allowedToPublish() {
			return this.projectHasPermission(this.projectId, ProjectPermissions.PublishWorkflows);
		},
		flowTitle() {
			return WorkflowService.getWorkflowName(this.editWorkflow);
		},
		isTaskLogOpen() {
			return this.useIsTaskLogOpen(this.$route);
		},
		isVersionHistoryOpen() {
			return !!this.useSelectedWorkflowVersionId;
		},
		hasWorkflowEverBeenPublished() {
			return this.editWorkflow?.is_published;
		},
		taskLogButtonTooltip() {
			return !this.hasWorkflowEverBeenPublished
				? 'Task log in debug mode is available only after publishing the workflow'
				: this.isTaskLogOpen
				? 'Close task log'
				: 'Open task log in debug mode';
		},
		canViewTaskLog() {
			return this.projectHasPermission(this.projectId, ProjectPermissions.ViewLogs);
		},
		isNormalLayout: {
			set(value) {
				const { isNormalMode } = useFlowLayoutMode(this.$router, this.$route);
				isNormalMode.value = value;
			},
			get() {
				const { isNormalMode } = useFlowLayoutMode(this.$router, this.$route);
				return isNormalMode.value;
			},
		},
	},
	methods: {
		...mapActions([updateWorkflow.fullPath]),
		...mapActionsShorthand({ selectProjectId, setLeftSideDrawerState }),
		closeLeftSideDrawerSpecialEditor() {
			WorkflowVersionService.closeLeftSideDrawerSpecialRouterView(this.$router);
		},
		openTaskLog() {
			if (this.isTaskLogOpen) {
				WorkflowVersionService.closeLeftSideDrawerSpecialRouterView(this.$router);
			} else {
				this.$router.push({ name: BUILDER_DEBUG_MODE_ROUTE_NAME });
			}
		},
		onSettingsClick() {
			this.openModuleSettingsDialog();
		},
		async openModuleSettingsDialog() {
			await AppDialogManagerService.openModuleSettingsDialog();
		},
		onSave(newV) {
			this.syncWorkflow({ workflow_title: newV });
		},
		async syncWorkflow(data) {
			await this[updateWorkflow.fullPath]({ id: this.editWorkflow.id, data }); // directly call store's updateWorkflow to be synced with server
		},
		isNodeStatusPublishValid(workflowNode) {
			const { service: serviceID } = workflowNode;
			const service = this[getService.fullPath](serviceID);
			if (service && AppService.isSlugOfWebhook(service.service_slug)) {
				// switch to handle special services like ply webhooks (default successful test result)
				return true;
			}
			if (WorkflowNodeService.isConsolidateDataNode(workflowNode, this.getAction)) {
				const branchVariables = workflowNode?.branch_variables || [];
				const areThereMappingsFromBranchVariables = branchVariables.some((branchVariable) => branchVariable.mappings.length > 0);
				if (!areThereMappingsFromBranchVariables) {
					return true;
				}
			}
			return isStatusPublishValid(workflowNode.test_result_status);
		},
		async testFlow() {
			if (this.isLoadedFromExtension) {
				IframeService.sendMessageToParent('test_flow');
			} else if (this.isLoadedFromEmbedded) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { sendMessage } = useEmbeddedApp(); // TODO
				// sendMessage({ action: EmbeddedAppActionTypes.TestFeature });
			} else {
				try {
					await openTestURLToolDialog({ workflowId: this.editWorkflow.id, mode: 'feature' });
				} catch (err) {
					if (err === 'cancel') {
						// user canceled the dialog
						return;
					}
					throw err;
				}
			}
		},
		closeBuilder() {
			if (this.isLoadedFromExtension) {
				IframeService.sendMessageToParent('flow_saved');
			} else if (this.isLoadedFromEmbedded) {
				const { sendMessage } = useEmbeddedApp();
				sendMessage({ action: EmbeddedAppActionTypes.Close });
			}
		},
	},
	watch: {
		editWorkflow: {
			handler(newV) {
				if (newV) {
					this.name = newV.workflow_title;
				}
			},
			immediate: true,
			deep: true,
		},
	},
	async mounted() {
		this.extSupportTest = await canShowTestFeatureButton();
		// we keep on mount the query params to know if we are loaded from extension or embedded, it cannot be via props because the query params change while the component is mounted
		this.isLoadedFromExtension = !!this.$route.query[IS_LOADED_FROM_EXTENSION_PARAM];
		this.isLoadedFromEmbedded = !!this.$route.query[IS_EMBEDDED_PARAM];
	},
	data() {
		return {
			extSupportTest: null,
			isLoadedFromEmbedded: null,
			isLoadedFromExtension: null,
		};
	},
	beforeDestroy() {
		if (this.projectIdToReturn) {
			this.selectProjectId({ id: this.projectIdToReturn });
		}
	},
};
</script>

<style scoped lang="scss">
@import './TopBar.style.scss';
.project-top-bar {
	height: $bar-height;
	background: white;
	z-index: $z-index-on-top-of-drawer;
	border-bottom: 1px solid var(--button-border-color);

	.ply-logo-container {
		height: 100%;
		width: $bar-height;
		background: transparent;
		border-radius: 1px !important;
		border-right: 1px solid var(--button-border-color);
	}

	.text-field-description {
		opacity: 0.5;
		font-size: 12px;
		color: var(--primary-font-color);
	}

	.setting-button {
		width: 30px !important;
		height: 30px !important;
		border-radius: 6px !important;
		background: transparent !important;
		border: 1px solid #d6dde8 !important;
	}
	.learn-button {
		width: 56px;
		background: transparent;
		border-radius: 0px !important;
		border-left: 1px solid var(--top-bar-button-border);
	}
}
.divider {
	width: 1px;
	height: 32px;
	background: #eceff5;
}
.builder-task-log-button {
	width: 30px !important;
	height: 30px !important;
	border-radius: 6px !important;
	background: var(--white) !important;
	border: 1px solid #d6dde8 !important;
}
.share-button {
	max-height: 30px !important;
}
.faded {
	opacity: 0.5;
}
.left-side-special-editor-indication {
	$right-side-width: 280px;
	width: 100%;
	position: absolute;
	margin-left: $right-side-width;
	padding-right: calc(2 * #{$right-side-width});
	.indication-container {
		height: 28px;
		width: fit-content;
		border-radius: 8px;
		background: #d6dde8;
		border: 1px solid #d6dde8;
		box-shadow: 0px 3px 6px var(--shadow-color);
	}
}
</style>
